"use client";
import { FieldPath, FieldValues, UseControllerReturn } from "react-hook-form";
import { Nil } from "cy-util";
import { useCallback, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";

export function useSelectAutoCompleteStatic<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TItem,
>(
  controller: UseControllerReturn<TFieldValues, TName>,
  items: TItem[] | Nil,
  filter: (query: string, item: TItem) => boolean,
  areEqual: (item1: TItem, item2: TItem) => boolean,
) {
  const [query, setQuery] = useState<string>("");
  const [debouncedQuery] = useDebounce(query, 250);
  const filterMemo = useCallback(filter, [filter]);

  const filteredList = useMemo(
    () =>
      (items || []).filter(
        (item) => !debouncedQuery || filterMemo(debouncedQuery, item),
      ),
    [filterMemo, items, debouncedQuery],
  );
  const value =
    items?.find(
      (x) =>
        controller.field.value != null && areEqual(controller.field.value, x),
    ) || null;
  if (controller.field.value && !value) {
    console.warn("Could not find item for value", controller.field.value);
  }
  return {
    selectProps: {
      ...controller.field,
      onQueryChange: setQuery,
      items: filteredList,
      value,
    },
  };
}

export function useSelectAutoCompleteApi<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TItem,
>(
  controller: UseControllerReturn<TFieldValues, TName>,
  items: TItem[] | Nil,
  filter: (query: string, item: TItem) => boolean,
  areEqual: (item1: TItem, item2: TItem) => boolean,
) {
  const [query, setQuery] = useState<string>("");
  const filteredList = (items || []).filter(
    (item) => !query || filter(query, item),
  );
  return {
    selectProps: {
      ...controller.field,
      onQueryChange: setQuery,
      items: filteredList,
      value: items?.find(
        (x) =>
          controller.field.value != null && areEqual(controller.field.value, x),
      ),
    },
  };
}
